import { encodingForModel } from "js-tiktoken";
import type { TiktokenModel } from "js-tiktoken";
import { EventBus } from "../../contexts";
import { logger, LOGS } from "../../utils";
import { ApiManager } from "../api";
import {
    AssistantProjectDTO,
    AssistantProjectInputDTO,
    AssistantUsageDTO,
    ConversationAgentResponseDTO,
    ConversationAvis,
    ConversationAvisResponseDTO,
    ConversationCompacterResponseDTO,
    ConversationDetailDTO,
    ConversationEtapesMemoireDTO,
    ConversationEtapesProjetDTO,
    ConversationEtapesResponseDTO,
    ConversationEtapesSuiteDTO,
    ConversationFactDTO,
    ConversationSummaryDTO,
    ConversationTurnSaveDTO,
    ScoreMailResponseDTO,
} from "../api/types/apiResponse";
import { AgentInvokeParams, AgentModelChoice } from "../conversation";
import { ViewFilter } from "../customView";
import { LicenseState } from "../license/LicenseState";
import type { EnrichedMail } from "../mail/types/mail.api";
import type { ScoringConfiguration } from "../mail/types/scoring";
import { UserProfileLoader } from "../user/UserProfileLoader";
import { OrganizationAiModelsResponse } from "./types/aiModel.api";
import {
    DROID_VOICE_ID,
    ELEVEN_LABS_API_KEY,
    ELEVEN_LABS_VOICE_ID,
    SpeechOptions,
    VOICE_SETTINGS,
    VOIX,
    XAVIER_VOICE_ID,
} from "./types/const";
import { TokenStatus } from "./types/ia";

export class IaManager {
    // #region Properties

    private eventBus: EventBus;

    private apiManager: ApiManager;

    private licenseState: LicenseState;

    private profileLoader: UserProfileLoader;

    private hash: number = Math.round(Math.random() * 100);

    // #endregion Properties

    constructor(
        eventBus: EventBus,
        apiManager: ApiManager,
        licenseState: LicenseState,
        profileLoader: UserProfileLoader,
    ) {
        logger.info([LOGS.IaManager], `IaManager(${this.hash})`);

        this.eventBus = eventBus;
        this.apiManager = apiManager;
        this.licenseState = licenseState;
        this.profileLoader = profileLoader;
    }

    // #region Initialisation and cleanup

    /**
     * @method cleanup
     * Cleans up all resources and stops all active processes for the IaManager
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {Promise<void>}
     * @public
     */
    public async _cleanup(): Promise<void> {
        logger.info(
            LOGS.IaManager,
            "IaManager.cleanup() - Starting cleanup process",
        );

        // Phase 1: Stop all active processes and timers
        try {
            logger.info(LOGS.IaManager, "  Phase 1: Stopping active processes");

            // No active timers, intervals, or polling to stop for IaManager
            // All operations are request-based
        } catch (error) {
            logger.error(
                LOGS.IaManager,
                `IaManager.cleanup() - Error in Phase 1: ${error}`,
            );
        }

        logger.info(
            LOGS.IaManager,
            "IaManager.cleanup() - Cleanup completed successfully",
        );
    }

    // #endregion Initialisation and cleanup

    // #region Event Handlers

    public _registerEvents() {
        this["ia-startup-token-status_handler"] =
            this["ia-startup-token-status_handler"].bind(this);
        this["ia-token-spent_handler"] =
            this["ia-token-spent_handler"].bind(this);

        this.eventBus.on(
            "ia-startup-token-status",
            this["ia-startup-token-status_handler"],
        );
        this.eventBus.on("ia-token-spent", this["ia-token-spent_handler"]);
    }

    private async "ia-startup-token-status_handler"(): Promise<void> {
        logger.info(
            LOGS.IaManager,
            `IaManager (${this.hash}) - processes ia-startup-token-status_handler`,
        );

        const { total, used } = this.getTokenStatus();
        if (total > 0 && used >= total) {
            logger.info(
                LOGS.IaManager,
                `  Monthly IA token quota already exhausted at startup (${used}/${total})`,
            );
            this.eventBus.emit("max-ia-tokens-reached");
        }
    }

    private async "ia-token-spent_handler"(spentTokens: number): Promise<void> {
        logger.info(
            LOGS.IaManager,
            `IaManager (${this.hash}) - processes ia-token-spent_handler`,
        );
        if (spentTokens > 0) {
            this.profileLoader.updateCachedProfile((profile) => ({
                ...profile,
                monthlySpentTokenCount: spentTokens,
            }));

            const { total, used } = this.getTokenStatus();
            if (total > 0 && used >= total) {
                logger.info(
                    LOGS.IaManager,
                    `  Monthly IA token quota reached (${used}/${total})`,
                );
                this.eventBus.emit("max-ia-tokens-reached");
            }
        }
    }

    // #endregion Event Handlers

    // #region Public Methods

    // #region Mail Processing

    public async generateAnswerWithAI(
        currentAnswer: string,
        body: string,
        speechOption: SpeechOptions,
        currentAnswerToReplace: string,
    ): Promise<string | null> {
        // const startTime = performance.now();
        logger.info(LOGS.IaManager, "generateAnswerWithAI ... ");

        const cleanedBody = this.cleanImagesAndSignatureFromHtml(body);
        const cleanedCurrentAnswer =
            this.cleanImagesAndSignatureFromHtml(currentAnswer);
        const cleanedCurrentAnswerToReplace =
            this.cleanImagesAndSignatureFromHtml(currentAnswerToReplace);

        logger.info(LOGS.IaManager, `cleanedBody : ${cleanedBody}`);
        logger.info(
            LOGS.IaManager,
            `cleanedCurrentAnswer : ${cleanedCurrentAnswer}`,
        );
        logger.info(
            LOGS.IaManager,
            `cleanedCurrentAnswerToReplace : ${cleanedCurrentAnswerToReplace}`,
        );

        // if (cleanedCurrentAnswerToReplace.length > 1500) {
        //     throw `Error in generateAnswerWithAI : Mail is too long to be treated by AI`;
        // }

        let result: string;
        try {
            result = await this.apiManager.callWritingAgent(
                cleanedCurrentAnswer,
                cleanedBody,
                speechOption,
                cleanedCurrentAnswerToReplace,
            );
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            logger.error(
                LOGS.IaManager,
                `Error in generateAnswerWithAI : ${errorMessage}`,
            );

            return null;
        }

        return result;
    }

    public cleanImagesAndSignatureFromHtml(htmlContent: string): string {
        if (!htmlContent?.trim()) {
            return "";
        }

        let cleaned = htmlContent;

        // Supprimer toutes les balises img (auto-fermantes et avec fermeture)
        cleaned = cleaned.replace(/<img[^>]*\/?>/gi, "");

        // Supprimer les balises table avec l'id "mailvista-signature"
        // Version 1: avec guillemets échappés (\")
        cleaned = cleaned.replace(
            /<table[^>]*\bid\s*=\s*\\"mailvista-signature\\"[^>]*>[\s\S]*?<\/table>/gi,
            "",
        );

        // Version 2: avec guillemets normaux (simples ou doubles)
        cleaned = cleaned.replace(
            /<table[^>]*\bid\s*=\s*["']mailvista-signature["'][^>]*>[\s\S]*?<\/table>/gi,
            "",
        );

        // Version 3: sans guillemets
        cleaned = cleaned.replace(
            /<table[^>]*\bid\s*=\s*mailvista-signature[^>]*>[\s\S]*?<\/table>/gi,
            "",
        );

        // Supprimer aussi les balises table avec id="signature" (toutes versions)
        cleaned = cleaned.replace(
            /<table[^>]*\bid\s*=\s*\\"signature\\"[^>]*>[\s\S]*?<\/table>/gi,
            "",
        );
        cleaned = cleaned.replace(
            /<table[^>]*\bid\s*=\s*["']signature["'][^>]*>[\s\S]*?<\/table>/gi,
            "",
        );
        cleaned = cleaned.replace(
            /<table[^>]*\bid\s*=\s*signature[^>]*>[\s\S]*?<\/table>/gi,
            "",
        );

        return cleaned;
    }

    public async scoringMail(
        mail: EnrichedMail,
        scoringConfiguration: ScoringConfiguration,
    ): Promise<ScoreMailResponseDTO> {
        try {
            const result = await this.apiManager.scoringMail(
                mail,
                scoringConfiguration,
            );

            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            logger.error(LOGS.IaManager, `Error in scoreMail: ${errorMessage}`);
            throw error;
        }
    }

    // #endregion Mail Processing

    //#region Token Management

    /**
     * Returns current user total token for the month and spent token for the month
     *
     * @returns TokenStatus
     */
    public getTokenStatus(): TokenStatus {
        const details = this.licenseState.details;

        return {
            total: details?.monthlyTokenCount ?? 0,
            used: details?.monthlySpentTokenCount ?? 0,
        };
    }

    public countTokens(
        input: string,
        model: TiktokenModel,
    ): { tokenCount: number; usageTokens: number; cost: number } {
        // Obtenir l'encodeur pour le modèle spécifié
        const encoder = encodingForModel(model);

        // Encoder la chaîne d'entrée en tokens
        const tokens = encoder.encode(input);

        // Obtenir le nombre de tokens
        const tokenCount = tokens.length;

        // Calculer le nombre de tokens d'utilisation
        const usageTokens = Math.ceil(tokenCount / 300);

        const cost = (tokenCount / 1000000) * 1.7911;

        // Retourner le nombre de tokens et les tokens d'utilisation
        return { tokenCount, usageTokens, cost };
    }

    //#endregion Token Management

    // #region Search Generation

    public async generateViewFilter(userPrompt: string): Promise<ViewFilter> {
        logger.info(LOGS.IaManager, "generateViewFilter ... ");

        const { total, used } = this.getTokenStatus();
        if (total > 0 && used >= total) {
            logger.error(
                LOGS.IaManager,
                `Monthly IA token limit reached (${used}/${total}) — generateViewFilter blocked`,
            );
            throw new Error("MONTHLY_IA_TOKEN_LIMIT_REACHED");
        }

        try {
            const result =
                await this.apiManager.callCustomViewAgent(userPrompt);

            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            logger.error(
                LOGS.IaManager,
                `Error in generateViewFilter: ${errorMessage}`,
            );
            throw error;
        }
    }

    // #endregion Search Generation

    // #region Discussion Processing

    /**
     * @param options.traceId - identifiant du tour, transmis à l'Api-Ai en `X-Mv-Trace-Id` (F-1.24).
     */
    public async processConversationWithAI(
        messages: Array<{ content: string; role: string }>,
        tools: any[],
        options?: { traceId?: string },
    ): Promise<ConversationAgentResponseDTO> {
        logger.info(LOGS.IaManager, "processConversationWithAI ... ");

        try {
            // tools est résolu côté serveur depuis le JWT — non transmis au nouvel endpoint
            const result = await this.apiManager.callConversationAgent(
                messages,
                options,
            );

            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            logger.error(
                LOGS.IaManager,
                `Error in processConversationWithAI: ${errorMessage}`,
            );
            throw error; // Propage l'erreur pour être gérée par ConversationManager
        }
    }

    /**
     * Un appel du contrat par étapes (docs/contrat-etapes.md de l'Api-Ai) : le même historique que
     * `processConversationWithAI`, plus `suite` (état du tour et résultats des étapes) à partir du
     * deuxième appel du tour, et la mémoire de la conversation (`memoire`, ses échanges).
     * @param options.traceId - identifiant du tour, le même pour tous ses appels (F-1.24).
     * @param options.signal - annulation du tour (« Nouvelle conversation ») : la requête est abandonnée.
     */
    public async processConversationEtapes(
        messages: Array<{ content: string; role: string }>,
        suite?: ConversationEtapesSuiteDTO,
        options?: {
            traceId?: string;
            signal?: AbortSignal;
            modele?: AgentModelChoice;
            projet?: ConversationEtapesProjetDTO;
            conversationId?: string;
        },
        memoire?: ConversationEtapesMemoireDTO,
    ): Promise<ConversationEtapesResponseDTO> {
        logger.info(LOGS.IaManager, "processConversationEtapes ... ");

        try {
            return await this.api().callConversationEtapes(
                messages,
                suite,
                options,
                memoire,
            );
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            logger.error(
                LOGS.IaManager,
                `Error in processConversationEtapes: ${errorMessage}`,
            );
            throw error; // Propage l'erreur pour être gérée par ConversationManager
        }
    }

    /**
     * Compacter la conversation (contrat par étapes) : l'Api-Ai résume ses échanges en un seul.
     */
    public async compactConversation(
        memoire: ConversationEtapesMemoireDTO,
        options?: { traceId?: string; conversationId?: string },
    ): Promise<ConversationCompacterResponseDTO> {
        return this.api().callConversationCompacter(memoire, options);
    }

    /** Historique des conversations : garde un tour (ou un compactage). */
    public async saveConversationTurn(
        turn: ConversationTurnSaveDTO,
    ): Promise<{ saved: boolean; position: number }> {
        return this.api().callConversationTurnSave(turn);
    }

    public async listConversations(
        query?: string,
    ): Promise<ConversationSummaryDTO[]> {
        return this.api().callConversationsList(query);
    }

    public async getConversation(id: string): Promise<ConversationDetailDTO> {
        return this.api().callConversationGet(id);
    }

    public async renameConversation(
        id: string,
        title: string,
    ): Promise<{ id: string; title: string }> {
        return this.api().callConversationRename(id, title);
    }

    public async deleteConversation(id: string): Promise<void> {
        return this.api().callConversationDelete(id);
    }

    public async deleteAllConversations(): Promise<void> {
        return this.api().callConversationsDeleteAll();
    }

    /** Compteur de consommation de l'assistant : le mois de l'utilisateur et une conversation. */
    public async getAssistantUsage(
        conversationId: string,
    ): Promise<AssistantUsageDTO> {
        return this.api().callAssistantUsage(conversationId);
    }

    /** Projets de l'assistant : la liste, la création, la modification, la suppression. */
    public async listProjects(): Promise<AssistantProjectDTO[]> {
        return this.api().callProjectsList();
    }

    public async createProject(
        project: AssistantProjectInputDTO,
    ): Promise<AssistantProjectDTO> {
        return this.api().callProjectCreate(project);
    }

    public async updateProject(
        id: string,
        changes: Partial<AssistantProjectInputDTO>,
    ): Promise<AssistantProjectDTO> {
        return this.api().callProjectUpdate(id, changes);
    }

    public async deleteProject(id: string): Promise<void> {
        return this.api().callProjectDelete(id);
    }

    /** Range une conversation de l'historique dans un projet, ou l'en sort (`null`). */
    public async setConversationProject(
        id: string,
        projectId: string | null,
    ): Promise<{ id: string; projectId: string | null }> {
        return this.api().callConversationSetProject(id, projectId);
    }

    /** Remplace les faits retenus dans une conversation de l'historique (liste vide : plus aucun). */
    public async setConversationMemory(
        id: string,
        faits: ConversationFactDTO[],
    ): Promise<{ id: string; faits: ConversationFactDTO[] }> {
        return this.api().callConversationSetMemory(id, faits);
    }

    private api(): ApiManager {
        if (!this.apiManager) {
            throw new Error("ApiManager is not set");
        }
        return this.apiManager;
    }

    /**
     * Pouce sous une réponse (contrat par étapes) : l'identifiant du tour et l'avis.
     */
    public async sendAnswerFeedback(
        traceId: string,
        avis: ConversationAvis,
    ): Promise<ConversationAvisResponseDTO> {
        return this.api().callConversationAvis(traceId, avis);
    }

    /**
     * Invoque l'agent d'analyse via l'ApiManager
     * @param agentInvokeParams - Paramètres pour l'invocation de l'agent
     * @returns Promise<string> - Résultat de l'analyse
     */
    public async invokeAnalysisAgent(
        agentInvokeParams: AgentInvokeParams,
    ): Promise<string> {
        logger.info(
            LOGS.IaManager,
            `invokeAnalysisAgent() - Processing analysis request for sessionId: ${agentInvokeParams.sessionId}`,
        );

        try {
            const result =
                await this.apiManager.callAnalysisAgent(agentInvokeParams);

            logger.info(
                LOGS.IaManager,
                `invokeAnalysisAgent() - Analysis completed for sessionId: ${agentInvokeParams.sessionId}`,
            );

            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            logger.error(
                LOGS.IaManager,
                `Error in invokeAnalysisAgent for sessionId ${agentInvokeParams.sessionId}: ${errorMessage}`,
            );
            throw error; // Propage l'erreur pour être gérée par ConversationManager
        }
    }

    public async elevenLabsTextToSpeech(
        text: string,
        voice: VOIX,
    ): Promise<boolean> {
        try {
            let voiceId = ELEVEN_LABS_VOICE_ID; // Default ALTIA voice

            switch (voice) {
                case VOIX.C3PO:
                    voiceId = DROID_VOICE_ID;
                    break;
                case VOIX.XAVIER:
                    voiceId = XAVIER_VOICE_ID;
                    break;
                default:
            }

            const response = await fetch(
                `https://api.elevenlabs.io/v1/text-to-speech/${voiceId}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "xi-api-key": ELEVEN_LABS_API_KEY,
                    },
                    body: JSON.stringify({
                        text,
                        voice_settings: VOICE_SETTINGS,
                        model_id: "eleven_turbo_v2_5",
                        language_code: "fr",
                    }),
                },
            );

            if (!response.ok) {
                throw new Error("Failed to generate speech");
            }

            const audioBlob = await response.blob();
            const audio = new Audio(URL.createObjectURL(audioBlob));

            return await new Promise<boolean>((resolve) => {
                audio.onended = () => {
                    resolve(true);
                };
                audio.play();
            });
        } catch (error) {
            logger.error(
                LOGS.ConversationManager,
                "ElevenLabs text-to-speech error:",
            );
            return false;
        }
    }

    public getPreviousDiscussion(body: string): string {
        const parts = body.split('<div id="appendonsend">');

        // Vérifier si parts existe et contient au moins 2 éléments
        if (!parts || parts.length < 2) {
            return "";
        }

        let cleanedContent = parts[1]; // Prendre la deuxième partie

        // Supprimer les balises de style et de script
        cleanedContent = cleanedContent
            .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
            .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "");

        // Supprimer les balises HTML mais conserver leur contenu
        cleanedContent = cleanedContent.replace(/<[^>]+>/g, " ");

        // Supprimer les espaces multiples
        cleanedContent = cleanedContent.replace(/\s+/g, " ");

        // Supprimer les espaces en début et fin de chaîne
        cleanedContent = cleanedContent.trim();

        // Supprimer les lignes vides
        cleanedContent = cleanedContent.replace(/^\s*[\r\n]/gm, "");

        // eslint-disable-next-line no-useless-escape
        cleanedContent = cleanedContent.replace(/\&nbsp;/g, "");

        // Limiter à 2000 caractères
        cleanedContent = cleanedContent.substring(0, 2000);

        return cleanedContent;
    }

    // #endregion Discussion Processing

    // #region Organization AI Model

    /**
     * Récupère le catalogue des modèles d'IA disponibles pour l'organisation
     * et le modèle actuellement sélectionné.
     * @returns Promise<OrganizationAiModelsResponse>
     */
    public async getOrganizationAiModel(): Promise<OrganizationAiModelsResponse> {
        logger.info(LOGS.IaManager, "getOrganizationAiModel ... ");

        try {
            return await this.apiManager.getOrganizationAiModel();
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            logger.error(
                LOGS.IaManager,
                `Error in getOrganizationAiModel: ${errorMessage}`,
            );
            throw error;
        }
    }

    /**
     * Définit le modèle d'IA actif pour l'organisation.
     * @param modelId - Identifiant du modèle à activer
     * @returns Promise<OrganizationAiModelsResponse> — même payload que getOrganizationAiModel
     */
    public async assignOrganizationAiModel(
        modelId: string,
    ): Promise<OrganizationAiModelsResponse> {
        logger.info(
            LOGS.IaManager,
            `assignOrganizationAiModel(${modelId}) ... `,
        );

        try {
            return await this.apiManager.assignOrganizationAiModel(modelId);
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            logger.error(
                LOGS.IaManager,
                `Error in assignOrganizationAiModel: ${errorMessage}`,
            );
            throw error;
        }
    }

    // #endregion Organization AI Model

    // #endregion Public Methods
}
